import { z } from 'zod';

/**
 * Contracten van de module shift-handover (Stap 39, OVD-1): de ploegoverdracht.
 *
 * Aan het einde van een dienst noteert de ploeg wat er gebeurd is, wat openstaat en welke machines
 * stilstaan. De volgende ploeg leest dat bij dienstbegin en tikt "Gelezen".
 *
 * Drie regels bepalen de vorm hieronder:
 *
 * 1. **Een open punt verdwijnt niet tot het gesloten is.** Er is geen veld om een punt weg te
 *    halen, alleen een status `closed` met wie en wanneer. Een nieuw concept neemt de open punten
 *    van de vorige overdracht over (`carriedSince`).
 * 2. **Eén concept per lijn en dienst**, niet per persoon. Een ander ploeglid kan het overnemen.
 * 3. **Aanpassen kan tot de eerste lezer van de volgende ploeg.** Daarna is de overdracht
 *    `locked` en kan er alleen nog een punt afgesloten worden.
 */

export const HANDOVER_MAX_PHOTOS = 6;

/** Draait, storing, stil. Altijd met icoon en woord getoond, nooit kleur alleen (spec §2). */
export const assetStatusValueSchema = z.enum(['running', 'fault', 'stopped']);
export type AssetStatusValue = z.infer<typeof assetStatusValueSchema>;

export const handoverStatusSchema = z.enum(['draft', 'submitted', 'locked']);
export type HandoverStatus = z.infer<typeof handoverStatusSchema>;

export const handoverItemStatusSchema = z.enum(['open', 'closed']);
export type HandoverItemStatus = z.infer<typeof handoverItemStatusSchema>;

/** `HH:MM` in de tijdzone van de vestiging van de lijn (of het bedrijf). */
const clockSchema = z
  .string()
  .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, 'Een tijd als 06:00, 14:00 of 22:00.');

// --- Beheer: machines en diensten -------------------------------------------------------

export const assetSchema = z.object({
  id: z.guid(),
  lineId: z.guid(),
  siteId: z.guid().nullable(),
  name: z.string(),
  position: z.number().int(),
  active: z.boolean(),
});
export type Asset = z.infer<typeof assetSchema>;

export const assetListSchema = z.object({ items: z.array(assetSchema) });
export type AssetList = z.infer<typeof assetListSchema>;

export const createAssetRequestSchema = z.object({
  lineId: z.guid(),
  name: z.string().trim().min(1, 'Geef de machine een naam.').max(80),
  position: z.number().int().min(0).max(999).optional(),
});
export type CreateAssetRequest = z.infer<typeof createAssetRequestSchema>;

export const updateAssetRequestSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  position: z.number().int().min(0).max(999).optional(),
  /** Uit dienst en niet verwijderd: een overdracht van vorig jaar noemt deze machine. */
  active: z.boolean().optional(),
});
export type UpdateAssetRequest = z.infer<typeof updateAssetRequestSchema>;

/**
 * Een dienst op een lijn: "Late ploeg, 14:00–22:00, de ploeg Lijn 3 · Late".
 *
 * Een dienst waarvan het einde vóór het begin ligt, loopt over middernacht (de nachtploeg). De
 * ploeg die dan werkt, is een groep; zonder groep is het iedereen van de lijn.
 */
export const shiftSlotSchema = z.object({
  id: z.guid(),
  lineId: z.guid(),
  name: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  crewGroupId: z.guid().nullable(),
  crewGroupName: z.string().nullable(),
  active: z.boolean(),
});
export type ShiftSlot = z.infer<typeof shiftSlotSchema>;

export const shiftSlotListSchema = z.object({ items: z.array(shiftSlotSchema) });
export type ShiftSlotList = z.infer<typeof shiftSlotListSchema>;

export const createShiftSlotRequestSchema = z
  .object({
    lineId: z.guid(),
    name: z.string().trim().min(1, 'Geef de dienst een naam.').max(60),
    startsAt: clockSchema,
    endsAt: clockSchema,
    crewGroupId: z.guid().nullable().default(null),
  })
  .refine((slot) => slot.startsAt !== slot.endsAt, {
    path: ['endsAt'],
    message: 'Een dienst duurt langer dan nul minuten.',
  });
export type CreateShiftSlotRequest = z.infer<typeof createShiftSlotRequestSchema>;

export const updateShiftSlotRequestSchema = z.object({
  name: z.string().trim().min(1).max(60).optional(),
  startsAt: clockSchema.optional(),
  endsAt: clockSchema.optional(),
  crewGroupId: z.guid().nullable().optional(),
  active: z.boolean().optional(),
});
export type UpdateShiftSlotRequest = z.infer<typeof updateShiftSlotRequestSchema>;

// --- De overdracht ------------------------------------------------------------------------

export const handoverItemSchema = z.object({
  id: z.guid(),
  text: z.string(),
  status: handoverItemStatusSchema,
  openedById: z.guid(),
  openedByName: z.string(),
  /** Wanneer het punt voor het eerst geopend werd, ook als het al drie keer doorschoof. */
  openedAt: z.string().datetime(),
  /** `true` als het punt uit een vorige overdracht komt ("Doorgeschoven sinds ma"). */
  carried: z.boolean(),
  closedByName: z.string().nullable(),
  closedAt: z.string().datetime().nullable(),
  closeNote: z.string(),
});
export type HandoverItem = z.infer<typeof handoverItemSchema>;

export const handoverAssetStatusSchema = z.object({
  assetId: z.guid(),
  name: z.string(),
  status: assetStatusValueSchema,
  note: z.string(),
});
export type HandoverAssetStatus = z.infer<typeof handoverAssetStatusSchema>;

export const handoverNextShiftSchema = z.object({
  slotId: z.guid(),
  slotName: z.string(),
  startsAt: z.string().datetime(),
});
export type HandoverNextShift = z.infer<typeof handoverNextShiftSchema>;

export const handoverSchema = z.object({
  id: z.guid(),
  lineId: z.guid(),
  lineName: z.string(),
  slotId: z.guid(),
  slotName: z.string(),
  /** De dag waarop de dienst begon; een nachtploeg van 22:00 hoort bij de dag ervoor. */
  shiftDate: z.string(),
  status: handoverStatusSchema,
  authorId: z.guid(),
  authorName: z.string(),
  /** Is de huidige gebruiker de schrijver? Zo niet, dan toont de app "Neem over". */
  isMine: z.boolean(),
  notes: z.string(),
  assets: z.array(handoverAssetStatusSchema),
  items: z.array(handoverItemSchema),
  fileIds: z.array(z.guid()),
  submittedAt: z.string().datetime().nullable(),
  lockedAt: z.string().datetime().nullable(),
  /** "Concept bewaard om 21:46". */
  updatedAt: z.string().datetime(),
  /** Mag de huidige gebruiker dit nog aanpassen? Nee zodra de volgende ploeg las. */
  editable: z.boolean(),
  next: handoverNextShiftSchema.nullable(),
  /** Wanneer de huidige gebruiker "Gelezen" tikte, of `null`. */
  myReadAt: z.string().datetime().nullable(),
});
export type Handover = z.infer<typeof handoverSchema>;

export const handoverListItemSchema = z.object({
  id: z.guid(),
  slotName: z.string(),
  shiftDate: z.string(),
  status: handoverStatusSchema,
  authorName: z.string(),
  submittedAt: z.string().datetime().nullable(),
  openItems: z.number().int(),
  stoppedAssets: z.number().int(),
  faultAssets: z.number().int(),
  readCount: z.number().int(),
});
export type HandoverListItem = z.infer<typeof handoverListItemSchema>;

export const handoverListSchema = z.object({ items: z.array(handoverListItemSchema) });
export type HandoverList = z.infer<typeof handoverListSchema>;

/**
 * De strook bovenaan Home, en de tekst van de melding: dezelfde zin op beide plekken (spec §3).
 *
 * `handoverId` is leeg als de vorige ploeg niets indiende; dan gaat de strook over de open punten
 * van de laatste overdracht die er wél was.
 */
export const handoverBannerSchema = z.object({
  handoverId: z.guid().nullable(),
  title: z.string(),
  facts: z.array(z.string()),
  submittedAt: z.string().datetime().nullable(),
  authorName: z.string().nullable(),
});
export type HandoverBanner = z.infer<typeof handoverBannerSchema>;

/** Voor de lezer: de laatste ingediende overdracht van een lijn, plus de stand. */
export const currentHandoverSchema = z.object({
  lineId: z.guid(),
  lineName: z.string(),
  handover: handoverSchema.nullable(),
  /** De dienst die vóór de huidige liep en niets indiende, of `null`. */
  missing: z.object({ slotName: z.string(), shiftDate: z.string() }).nullable(),
  /** Wat er nu openstaat op deze lijn, ook als de vorige ploeg niets indiende. */
  openItems: z.array(handoverItemSchema),
  /** `null` als er voor de huidige gebruiker niets te lezen valt. */
  banner: handoverBannerSchema.nullable(),
});
export type CurrentHandover = z.infer<typeof currentHandoverSchema>;

/** De lijnen waar de huidige gebruiker bij hoort, voor de keuzechips (spec §5). */
export const myHandoverLineSchema = z.object({
  lineId: z.guid(),
  lineName: z.string(),
  slots: z.array(shiftSlotSchema),
  /** De dienst die nu loopt: daarvoor schrijft wie nu "Overdracht schrijven" tikt. */
  currentSlotId: z.guid().nullable(),
  banner: handoverBannerSchema.nullable(),
});
export type MyHandoverLine = z.infer<typeof myHandoverLineSchema>;

export const myHandoverLineListSchema = z.object({ items: z.array(myHandoverLineSchema) });
export type MyHandoverLineList = z.infer<typeof myHandoverLineListSchema>;

/**
 * Het concept bewaren (autosave).
 *
 * `PUT`, en de punten dragen een id dat de app zelf maakt: een autosave die twee keer aankomt,
 * hoort één punt op te leveren en niet twee. Een punt dat niet in de lijst staat, blijft gewoon
 * bestaan - er is geen weg om een punt weg te halen.
 */
export const saveHandoverRequestSchema = z.object({
  notes: z.string().max(4000).default(''),
  assets: z
    .array(
      z.object({
        assetId: z.guid(),
        status: assetStatusValueSchema,
        note: z.string().trim().max(500).default(''),
      }),
    )
    .max(200)
    .default([]),
  items: z
    .array(
      z.object({
        id: z.guid(),
        text: z.string().trim().min(1, 'Een punt heeft een tekst.').max(500),
        status: handoverItemStatusSchema,
        closeNote: z.string().trim().max(500).default(''),
      }),
    )
    .max(100)
    .default([]),
  fileIds: z.array(z.guid()).max(HANDOVER_MAX_PHOTOS).default([]),
  /** Bewust overnemen van het concept van een collega ("Neem over"). Anders `409`. */
  takeOver: z.boolean().default(false),
});
export type SaveHandoverRequest = z.infer<typeof saveHandoverRequestSchema>;

export const closeHandoverItemRequestSchema = z.object({
  note: z.string().trim().max(500).default(''),
});
export type CloseHandoverItemRequest = z.infer<typeof closeHandoverItemRequestSchema>;

/**
 * Wie las de overdracht.
 *
 * De teamleider van de ploeg krijgt `people` met namen (gelezen met tijdstip, nog niet gelezen
 * bovenaan); een collega krijgt alleen de aantallen en `people: null` (spec §1).
 */
export const handoverReadersSchema = z.object({
  expected: z.number().int(),
  read: z.number().int(),
  people: z
    .array(
      z.object({
        personId: z.guid(),
        name: z.string(),
        readAt: z.string().datetime().nullable(),
      }),
    )
    .nullable(),
});
export type HandoverReaders = z.infer<typeof handoverReadersSchema>;
